/**
 * Frontend error reporter.
 *
 * Wires global ``error`` and ``unhandledrejection`` listeners on
 * ``window`` and ships matched errors to ``POST /api/v1/errors/report``.
 * The reporter is intentionally tiny — no batching, no retry — because
 * the admin panel deduplicates by fingerprint on the server.
 *
 * Two correctness rules:
 *
 *  1. Never throw out of any handler. A reporter that breaks the page
 *     would make the dashboard useless.
 *  2. Never report errors generated by the reporter itself; a 5xx on
 *     ``/errors/report`` must not generate another report. We tag the
 *     fetch with a sentinel and skip it inside the handlers.
 */

import { postErrorReport } from '@/lib/api/endpoints';
import type { FrontendErrorReport } from '@/types';

const REPORT_PATH = '/api/v1/errors/report';
const SESSION_STORAGE_KEY = 'tt:err:session';

let installed = false;

function generateSessionId(): string {
  const buf = new Uint8Array(16);
  if (typeof crypto !== 'undefined') {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage may be disabled (private browsing). Fall back to a
    // per-page-load id so reports are still deduplicated within one tab.
    return generateSessionId();
  }
}

function truncate(value: string | undefined, limit: number): string | undefined {
  if (!value) return undefined;
  return value.length > limit ? value.slice(0, limit - 1) + '…' : value;
}

function buildReport(
  name: string,
  message: string,
  stack: string | undefined,
  extras: Partial<FrontendErrorReport> = {},
): FrontendErrorReport {
  return {
    name: truncate(name, 200) ?? 'Error',
    message: truncate(message, 1000) ?? '(no message)',
    stack: truncate(stack, 20000),
    url: window.location.pathname + window.location.search,
    severity: 'error',
    session_id: getSessionId(),
    user_agent: navigator.userAgent,
    ...extras,
  };
}

async function send(report: FrontendErrorReport): Promise<void> {
  try {
    await postErrorReport(report);
  } catch {
    // Swallowed by design — a failed report must not generate another.
  }
}

function isReporterTriggered(error: unknown): boolean {
  // If the error originated from our own fetch to /errors/report, skip it.
  if (error instanceof Error && error.message.includes(REPORT_PATH)) return true;
  return false;
}

export function installErrorReporter(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      const err = event.error as unknown;
      if (isReporterTriggered(err)) return;
      const name = err instanceof Error ? err.name : 'Error';
      const message = err instanceof Error ? err.message : event.message;
      const stack = err instanceof Error ? err.stack : undefined;
      void send(buildReport(name, message, stack));
    } catch {
      // Never let the handler itself blow up.
    }
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason as unknown;
      if (isReporterTriggered(reason)) return;
      const name = reason instanceof Error ? reason.name : 'UnhandledRejection';
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : JSON.stringify(reason);
      const stack = reason instanceof Error ? reason.stack : undefined;
      void send(buildReport(name, message, stack, { severity: 'error' }));
    } catch {
      // Never let the handler itself blow up.
    }
  });
}

/** Imperative helper for callers (error boundaries) that already hold the Error. */
export function reportError(error: Error, extras: Partial<FrontendErrorReport> = {}): void {
  try {
    void send(buildReport(error.name, error.message, error.stack, extras));
  } catch {
    // Swallow.
  }
}
